/**
 * Utility functions for BMI calculation
 */

/**
 * Calculate BMI from height and weight
 * @param {number} heightCm - Height in centimeters
 * @param {number} weightKg - Weight in kilograms
 * @returns {number} BMI value rounded to 1 decimal place
 */
export const calculateBMI = (heightCm, weightKg) => {
  if (!heightCm || !weightKg || heightCm <= 0 || weightKg <= 0) return null

  // Convert height from cm to meters
  const heightM = heightCm / 100

  // BMI formula: weight (kg) / (height (m))^2
  const bmi = weightKg / (heightM * heightM)

  // Round to 1 decimal place
  return Math.round(bmi * 10) / 10
}

/**
 * Process patient data entries to calculate BMI from height and weight
 * @param {Array} patientDataEntries - Array of patient data entries
 * @returns {Array} Updated patient data entries with calculated BMI
 */
export const processPatientDataWithBMI = (patientDataEntries) => {
  if (!patientDataEntries || !Array.isArray(patientDataEntries)) {
    return []
  }

  // Create a copy of the entries to avoid mutating the original
  let processedEntries = [...patientDataEntries]

  // Remove any existing BMI entries - we'll calculate them fresh
  processedEntries = processedEntries.filter((entry) => entry.key !== 'bmi')

  // Get height and weight entries
  const heightEntries = processedEntries.filter((entry) => entry.key === 'height')
  const weightEntries = processedEntries.filter((entry) => entry.key === 'weight')

  // If we don't have both height and weight, return the original entries
  if (heightEntries.length === 0 || weightEntries.length === 0) {
    return processedEntries
  }

  // Group height and weight entries by date (using the day as the key)
  const heightByDate = {}
  const weightByDate = {}

  // Process height entries
  heightEntries.forEach((entry) => {
    const date = new Date(entry.createdAt || entry.dateCreated)
    const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

    if (
      !heightByDate[dateKey] ||
      new Date(entry.createdAt || entry.dateCreated) >
        new Date(heightByDate[dateKey].createdAt || heightByDate[dateKey].dateCreated)
    ) {
      heightByDate[dateKey] = entry
    }
  })

  // Process weight entries
  weightEntries.forEach((entry) => {
    const date = new Date(entry.createdAt || entry.dateCreated)
    const dateKey = `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

    if (
      !weightByDate[dateKey] ||
      new Date(entry.createdAt || entry.dateCreated) >
        new Date(weightByDate[dateKey].createdAt || weightByDate[dateKey].dateCreated)
    ) {
      weightByDate[dateKey] = entry
    }
  })

  // Find all dates where we have both height and weight
  const allDates = [...new Set([...Object.keys(heightByDate), ...Object.keys(weightByDate)])]

  // For each date, if we have both height and weight, calculate BMI
  allDates.forEach((dateKey) => {
    const heightEntry = heightByDate[dateKey]
    const weightEntry = weightByDate[dateKey]

    // If we have both height and weight for this date, calculate BMI
    if (heightEntry && weightEntry) {
      const bmi = calculateBMI(heightEntry.value, weightEntry.value)

      if (bmi) {
        // Use the latest timestamp between height and weight
        const heightTimestamp = new Date(heightEntry.createdAt || heightEntry.dateCreated).getTime()
        const weightTimestamp = new Date(weightEntry.createdAt || weightEntry.dateCreated).getTime()
        const latestTimestamp = new Date(Math.max(heightTimestamp, weightTimestamp))

        // Add BMI entry
        processedEntries.push({
          key: 'bmi',
          value: bmi,
          createdAt: latestTimestamp,
          // Add references to the source height and weight entries
          sourceHeight: heightEntry.value,
          sourceWeight: weightEntry.value,
          calculated: true // Mark as calculated
        })
      }
    }
  })

  // If we don't have matching dates but we have at least one height and one weight,
  // calculate BMI using the latest values
  if (!processedEntries.some((entry) => entry.key === 'bmi')) {
    // Sort entries by date (newest first)
    heightEntries.sort((a, b) => new Date(b.createdAt || b.dateCreated) - new Date(a.createdAt || a.dateCreated))
    weightEntries.sort((a, b) => new Date(b.createdAt || b.dateCreated) - new Date(a.createdAt || a.dateCreated))

    // Get the latest height and weight
    const latestHeight = heightEntries[0].value
    const latestWeight = weightEntries[0].value

    // Calculate BMI
    const bmi = calculateBMI(latestHeight, latestWeight)

    if (bmi) {
      // Add BMI entry with the same timestamp as the most recent measurement
      const heightTimestamp = new Date(heightEntries[0].createdAt || heightEntries[0].dateCreated).getTime()
      const weightTimestamp = new Date(weightEntries[0].createdAt || weightEntries[0].dateCreated).getTime()
      const latestTimestamp = new Date(Math.max(heightTimestamp, weightTimestamp))

      // Add BMI entry
      processedEntries.push({
        key: 'bmi',
        value: bmi,
        createdAt: latestTimestamp,
        // Add references to the source height and weight entries
        sourceHeight: latestHeight,
        sourceWeight: latestWeight,
        calculated: true // Mark as calculated
      })
    }
  }

  return processedEntries
}
